import React from "react";
import { Link } from "react-router-dom";

const COMPANY_NAME = "SSQUARE LOGIC";
const PRODUCT_NAME = "StayServe";

const sections = [
  {
    number: "01",
    title: "Acceptance of Terms",
    content: (
      <>
        <p>
          By accessing or using {PRODUCT_NAME}, you agree to be bound by these
          Terms & Conditions. If you do not agree with these terms, please do
          not use the platform or its services.
        </p>

        <p>
          These Terms apply to restaurants, hotels, hospitality businesses,
          their owners, administrators, employees, authorized users and other
          customers who access or use {PRODUCT_NAME}.
        </p>
      </>
    ),
  },
  {
    number: "02",
    title: "About StayServe",
    content: (
      <>
        <p>
          {PRODUCT_NAME} is a hospitality technology platform developed and
          operated by {COMPANY_NAME}. The platform is designed to help
          restaurants, hotels and hospitality businesses manage operations
          such as orders, billing, QR ordering, kitchen workflows, inventory,
          customers, reservations and reporting.
        </p>

        <p>
          Features may vary depending on the plan, configuration and services
          available to a particular customer.
        </p>
      </>
    ),
  },
  {
    number: "03",
    title: "Account Registration",
    content: (
      <>
        <p>
          You may be required to create an account to access certain features.
          You agree to provide accurate, current and complete information
          during registration and to keep that information updated.
        </p>

        <p>
          You are responsible for maintaining the confidentiality of your
          login credentials and for all activities performed through your
          account.
        </p>

        <p>
          You must immediately notify {COMPANY_NAME} if you believe that your
          account has been accessed without authorization.
        </p>
      </>
    ),
  },
  {
    number: "04",
    title: "Authorized Use",
    content: (
      <>
        <p>You agree to use {PRODUCT_NAME} only for lawful business purposes.</p>

        <ul>
          <li>Do not use the platform for unlawful activities.</li>
          <li>Do not attempt to gain unauthorized access to the system.</li>
          <li>Do not interfere with the security or operation of the platform.</li>
          <li>Do not upload malicious code, malware or harmful content.</li>
          <li>Do not misuse another customer's account or information.</li>
          <li>Do not attempt to reverse engineer or exploit the platform.</li>
        </ul>
      </>
    ),
  },
  {
    number: "05",
    title: "Subscriptions and Plans",
    content: (
      <>
        <p>
          Certain features of {PRODUCT_NAME} may be available through paid
          subscriptions or other paid plans.
        </p>

        <p>
          The applicable price, billing period, included features, credits and
          other plan conditions will be displayed to you before purchase.
        </p>

        <p>
          {COMPANY_NAME} may introduce, modify or discontinue plans or
          features from time to time, subject to applicable law and any
          commitments already made to customers.
        </p>
      </>
    ),
  },
  {
    number: "06",
    title: "Payments",
    content: (
      <>
        <p>
          Payments may be processed through third-party payment providers,
          including Razorpay, as well as supported direct UPI payment
          methods.
        </p>

        <p>
          Payment processing may be subject to the terms, privacy policies and
          security practices of the applicable payment provider.
        </p>

        <p>
          {COMPANY_NAME} does not store complete card numbers, CVV numbers or
          other sensitive payment credentials on its own systems unless
          expressly stated otherwise.
        </p>

        <p>
          Customers are responsible for ensuring that payment information
          provided during a transaction is accurate and authorized.
        </p>
      </>
    ),
  },
  {
    number: "07",
    title: "Trial Offers",
    content: (
      <>
        <p>
          If a trial or introductory offer is available, its duration, price
          and renewal terms will be clearly displayed before activation.
        </p>

        <p>
          Trial offers may be subject to eligibility requirements and may be
          modified or withdrawn where permitted by applicable law.
        </p>
      </>
    ),
  },
  {
    number: "08",
    title: "Customer Data and Content",
    content: (
      <>
        <p>
          Customers may enter business information, menus, product details,
          employee information, customer information, orders and other
          operational data into the platform.
        </p>

        <p>
          You retain responsibility for the accuracy, legality and
          appropriateness of information that you submit to {PRODUCT_NAME}.
        </p>

        <p>
          You must ensure that you have the necessary rights, permissions and
          lawful basis to provide any personal information that you submit to
          the platform.
        </p>
      </>
    ),
  },
  {
    number: "09",
    title: "Intellectual Property",
    content: (
      <>
        <p>
          The {PRODUCT_NAME} platform, software, interface, design, branding,
          graphics, documentation and related materials are owned by or
          licensed to {COMPANY_NAME} and are protected by applicable
          intellectual property laws.
        </p>

        <p>
          Except where expressly permitted, you may not copy, reproduce,
          distribute, modify, sell, sublicense or commercially exploit any
          part of the platform.
        </p>
      </>
    ),
  },
  {
    number: "10",
    title: "Third-Party Services",
    content: (
      <>
        <p>
          {PRODUCT_NAME} may integrate with third-party services such as
          payment providers, mapping services, communication services,
          analytics providers, hosting providers or other external platforms.
        </p>

        <p>
          Your use of those services may also be governed by the applicable
          third party's terms and policies.
        </p>
      </>
    ),
  },
  {
    number: "11",
    title: "Service Availability",
    content: (
      <>
        <p>
          We aim to keep {PRODUCT_NAME} available and reliable, but we do not
          guarantee uninterrupted or error-free operation at all times.
        </p>

        <p>
          Service availability may be affected by maintenance, upgrades,
          network failures, third-party services, infrastructure issues or
          circumstances outside our reasonable control.
        </p>
      </>
    ),
  },
  {
    number: "12",
    title: "Suspension or Termination",
    content: (
      <>
        <p>
          We may suspend or restrict access to an account where reasonably
          necessary to protect the platform, other users, our services or to
          comply with applicable law.
        </p>

        <p>
          Customers may stop using the service or request account closure in
          accordance with the applicable process.
        </p>
      </>
    ),
  },
  {
    number: "13",
    title: "Disclaimers",
    content: (
      <>
        <p>
          {PRODUCT_NAME} is provided as a technology platform to support
          hospitality operations. It does not replace the customer's own
          business processes, legal obligations, accounting controls, tax
          responsibilities or operational decisions.
        </p>

        <p>
          Customers remain responsible for verifying transactions, reports,
          tax information, inventory information and other business data
          generated or processed through the platform.
        </p>
      </>
    ),
  },
  {
    number: "14",
    title: "Limitation of Liability",
    content: (
      <p>
        To the maximum extent permitted by applicable law, {COMPANY_NAME} will
        not be responsible for indirect, incidental, special or consequential
        losses arising from the use of the platform, including losses caused
        by circumstances outside our reasonable control.
      </p>
    ),
  },
  {
    number: "15",
    title: "Indemnification",
    content: (
      <p>
        To the extent permitted by applicable law, you agree to indemnify and
        hold harmless {COMPANY_NAME} and its personnel from claims, losses or
        liabilities arising from your unlawful use of the platform, violation
        of these Terms, or violation of third-party rights.
      </p>
    ),
  },
  {
    number: "16",
    title: "Changes to These Terms",
    content: (
      <p>
        We may update these Terms from time to time to reflect changes to the
        platform, business practices, technology or applicable requirements.
        Updated Terms will be published on this page with an updated effective
        date where appropriate.
      </p>
    ),
  },
  {
    number: "17",
    title: "Governing Law",
    content: (
      <p>
        These Terms shall be governed by the applicable laws of India. Any
        dispute shall be subject to the jurisdiction of the courts having
        appropriate jurisdiction over {COMPANY_NAME}, subject to applicable
        law.
      </p>
    ),
  },
  {
    number: "18",
    title: "Contact Us",
    content: (
      <>
        <p>
          If you have questions regarding these Terms & Conditions, you may
          contact {COMPANY_NAME} through the contact details provided on the
          website.
        </p>

        <Link
          to="/#contact"
          className="inline-flex mt-4 rounded-xl bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-600"
        >
          Contact {COMPANY_NAME} →
        </Link>
      </>
    ),
  },
];

export default function TermsAndConditions() {
  return (
    <main className="min-h-screen bg-stone-50 text-stone-900">
      {/* Header */}
      <section className="border-b border-stone-200 bg-stone-950 text-white">
        <div className="mx-auto max-w-6xl px-6 py-20 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-orange-400 transition hover:text-orange-300"
          >
            ← Back to {PRODUCT_NAME}
          </Link>

          <div className="mt-10 max-w-4xl">
            <div className="mb-5 inline-flex rounded-full border border-orange-500/30 bg-orange-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-orange-400">
              Legal
            </div>

            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              Terms & Conditions
            </h1>

            <p className="mt-6 max-w-3xl text-lg leading-8 text-stone-300">
              These terms explain the rules and conditions governing your use
              of {PRODUCT_NAME}, operated by {COMPANY_NAME}.
            </p>

            <p className="mt-6 text-sm text-stone-500">
              Effective Date: 18 August 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="mx-auto max-w-5xl px-6 py-16 lg:px-8">
        <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm sm:p-10">
          <div className="space-y-12">
            {sections.map((section) => (
              <section
                key={section.number}
                className="border-b border-stone-200 pb-10 last:border-b-0 last:pb-0"
              >
                <div className="flex gap-5">
                  <div className="hidden shrink-0 text-sm font-bold text-orange-500 sm:block">
                    {section.number}
                  </div>

                  <div className="min-w-0 flex-1">
                    <h2 className="text-2xl font-bold tracking-tight text-stone-950">
                      {section.title}
                    </h2>

                    <div className="mt-5 space-y-4 text-[15px] leading-7 text-stone-600">
                      {section.content}
                    </div>
                  </div>
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="mx-auto max-w-5xl px-6 pb-16 lg:px-8">
        <div className="rounded-3xl bg-stone-950 px-8 py-12 text-center text-white">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-orange-400">
            {COMPANY_NAME}
          </p>

          <h2 className="mt-3 text-3xl font-bold">
            Questions about these terms?
          </h2>

          <p className="mx-auto mt-4 max-w-2xl text-stone-400">
            Our team is available to help clarify questions regarding
            {PRODUCT_NAME} and its services.
          </p>

          <Link
            to="/#contact"
            className="mt-7 inline-flex rounded-xl bg-orange-500 px-6 py-3 font-semibold text-white transition hover:bg-orange-600"
          >
            Contact Us →
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-stone-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-6 py-8 text-sm text-stone-500 sm:flex-row sm:items-center sm:justify-between lg:px-8">
          <p>
            © {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.
          </p>

          <div className="flex flex-wrap gap-5">
            <Link
              to="/privacy-policy"
              className="transition hover:text-orange-500"
            >
              Privacy Policy
            </Link>

            <Link
              to="/terms-and-conditions"
              className="text-orange-500"
            >
              Terms & Conditions
            </Link>

            <Link
              to="/refund-policy"
              className="transition hover:text-orange-500"
            >
              Refund Policy
            </Link>

            <Link
              to="/cookie-policy"
              className="transition hover:text-orange-500"
            >
              Cookie Policy
            </Link>
          </div>
        </div>
      </footer>
    </main>
  );
}